import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import SammiLogo from '../components/common/SammiLogo';
import Input from '../components/common/Input';
import Button from '../components/common/Button';
import Section from '../components/common/Section';
import { OrganizationData } from '../types/Registration';

export const RegisterOrganizationPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const [organizationData, setOrganizationData] = useState<OrganizationData>({
    name: '',
    phoneNumber: '',
    email: ''
  });

  useEffect(() => {
    // Load existing organization data if available (user came back to edit)
    const existingOrgData = localStorage.getItem('pendingOrganization');
    if (existingOrgData) {
      try {
        const orgData = JSON.parse(existingOrgData);
        setOrganizationData(orgData);
      } catch (error) {
        console.error('Error parsing existing organization data:', error);
      }
    }
  }, []);

  const updateOrganization = (field: keyof OrganizationData, value: string) => {
    setOrganizationData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const validateForm = (): boolean => {
    // Check required organization fields
    if (!organizationData.name || !organizationData.phoneNumber || !organizationData.email) {
      setErrorMessage('Please fill in all required organization fields.');
      return false;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(organizationData.email)) {
      setErrorMessage('Please enter a valid email address.');
      return false;
    }

    setErrorMessage('');
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setErrorMessage('');

    try {
      // Store organization data in localStorage to pass to next step
      localStorage.setItem('pendingOrganization', JSON.stringify(organizationData));
      
      // Navigate to admin user setup
      navigate('/register-admin');
    } catch (error) {
      setErrorMessage('An error occurred. Please try again.');
      console.error('Organization setup error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div 
      className="min-h-screen flex flex-col items-center justify-start py-4 px-4 overflow-auto"
      style={{
        background: 'linear-gradient(135deg, rgba(25, 183, 223, 0.75) 0%, rgba(65, 239, 170, 0.75) 100%), linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)'
      }}
    >
      <div className="flex flex-col items-center justify-center bg-white/80 backdrop-blur-sm rounded-3xl shadow-2xl p-4 md:p-8 lg:p-12 w-full max-w-xl mx-auto my-auto">
        {/* Header */}
        <div className="flex items-center justify-between w-full mb-4 md:mb-6">
          <Link 
            to="/welcome" 
            className="flex items-center transition-colors duration-200"
            style={{ color: '#002457' }}
            onMouseEnter={(e) => (e.currentTarget as HTMLAnchorElement).style.color = '#41EFAA'}
            onMouseLeave={(e) => (e.currentTarget as HTMLAnchorElement).style.color = '#002457'}
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            Back
          </Link>
        </div>

        <SammiLogo />
        
        <h2 className="text-xl md:text-2xl font-bold mb-1 md:mb-2 text-center" style={{ color: '#002457' }}>
          Create Your Organization
        </h2>
        <p className="text-gray-600 mb-6 md:mb-8 text-center text-sm md:text-base">
          Step 1 of 3: Organization Information
        </p>

        <form onSubmit={handleSubmit} className="w-full">
          {/* Organization Information */}
          <Section title="Organization Details">
            <Input
              id="orgName"
              label="Organization Name"
              value={organizationData.name}
              onChange={(value) => updateOrganization('name', value)}
              placeholder="Enter your organization name"
              required
              autoComplete="organization"
            />
            
            <Input
              id="orgPhone"
              label="Organization Phone"
              type="tel"
              value={organizationData.phoneNumber}
              onChange={(value) => updateOrganization('phoneNumber', value)}
              placeholder="(555) 123-4567"
              required
              autoComplete="tel"
              formatPhone
            />
            
            <Input
              id="orgEmail"
              label="Organization Email"
              type="email"
              value={organizationData.email}
              onChange={(value) => updateOrganization('email', value)}
              placeholder="contact@yourcompany.com"
              required
              autoComplete="email"
            />
          </Section>

          {errorMessage && (
            <div className="mb-6 p-4 rounded-2xl bg-red-500 text-white text-center text-sm">
              {errorMessage}
            </div>
          )}

          <div className="flex flex-col space-y-4">
            <Button
              variant="primary"
              type="submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Processing...' : 'Continue to Admin Setup'}
            </Button>
            
            <div className="text-center">
              <span className="text-gray-600 text-sm">Already have an account? </span>
              <Link 
                to="/login" 
                className="text-sm transition-colors duration-200"
                style={{ color: '#002457', textDecoration: 'none' }}
                onMouseEnter={(e) => (e.currentTarget as HTMLAnchorElement).style.color = '#41EFAA'}
                onMouseLeave={(e) => (e.currentTarget as HTMLAnchorElement).style.color = '#002457'}
              >
                Sign in here
              </Link>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RegisterOrganizationPage;
